import {
  addRxPlugin,
  createRxDatabase,
  type RxCollection,
  type RxDatabase,
} from 'rxdb';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { RxDBMigrationSchemaPlugin } from 'rxdb/plugins/migration-schema';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
import {
  rxdbModels,
  DIARY_DAYS_COLLECTION,
  type DiaryDayDocType,
  DIARY_DAY_OUTBOX_COLLECTION,
  type DiaryDayOutboxDocType,
  RECIPES_COLLECTION,
  type RecipeDocType,
  USER_FOODS_COLLECTION,
  type UserFoodDocType,
  GOALS_HISTORY_COLLECTION,
  type GoalHistoryDocType,
  WEIGHT_HISTORY_COLLECTION,
  type WeightHistoryDocType,
  CUSTOM_FOOD_MIGRATIONS_COLLECTION,
  type CustomFoodMigrationDocType,
  SETTINGS_COLLECTION,
  type SettingDocType,
  MEALS_COLLECTION,
  type MealsConfigurationDocType,
} from '@macro-tracker/models';
import { createDiaryDayConflictHandler } from './diaryConflictHandler';

// Load dev-mode plugin in development, but not during Vitest runs because
// dev-mode requires a schema-validator plugin which adds bundle weight we
// don't want in the test runner. Schema validation still happens via TypeScript.
if (import.meta.env.DEV && !import.meta.env.VITEST) {
  const { RxDBDevModePlugin } = await import('rxdb/plugins/dev-mode');
  addRxPlugin(RxDBDevModePlugin);
}

addRxPlugin(RxDBMigrationSchemaPlugin);
addRxPlugin(RxDBQueryBuilderPlugin);

export type AppCollections = {
  [DIARY_DAYS_COLLECTION]: RxCollection<DiaryDayDocType>;
  [DIARY_DAY_OUTBOX_COLLECTION]: RxCollection<DiaryDayOutboxDocType>;
  [RECIPES_COLLECTION]: RxCollection<RecipeDocType>;
  [USER_FOODS_COLLECTION]: RxCollection<UserFoodDocType>;
  [GOALS_HISTORY_COLLECTION]: RxCollection<GoalHistoryDocType>;
  [WEIGHT_HISTORY_COLLECTION]: RxCollection<WeightHistoryDocType>;
  [CUSTOM_FOOD_MIGRATIONS_COLLECTION]: RxCollection<CustomFoodMigrationDocType>;
  [SETTINGS_COLLECTION]: RxCollection<SettingDocType>;
  [MEALS_COLLECTION]: RxCollection<MealsConfigurationDocType>;
};
export type AppDatabase = RxDatabase<AppCollections>;

const dbCache = new Map<string, Promise<AppDatabase>>();

const sanitizeName = (userId: string): string =>
  `macrotracker_user_${userId.replace(/[^a-z0-9]/gi, '_').toLowerCase()}`;

async function getStorage() {
  const storage = getRxStorageDexie();

  if (import.meta.env.DEV && !import.meta.env.VITEST) {
    const { wrappedValidateAjvStorage } = await import('rxdb/plugins/validate-ajv');
    return wrappedValidateAjvStorage({ storage });
  }

  return storage;
}

export async function getDb(userId: string): Promise<AppDatabase> {
  const cached = dbCache.get(userId);
  if (cached) return cached;

  const promise = (async () => {
    let db: AppDatabase | null = null;

    try {
      db = await createRxDatabase<AppCollections>({
        name: sanitizeName(userId),
        storage: await getStorage(),
        multiInstance: true,
        eventReduce: true,
        ignoreDuplicate: false,
      });

      await db.addCollections({
        [DIARY_DAYS_COLLECTION]: {
          schema: rxdbModels[DIARY_DAYS_COLLECTION].schema,
          migrationStrategies: rxdbModels[DIARY_DAYS_COLLECTION].migrationStrategies,
          autoMigrate: true,
          conflictHandler: createDiaryDayConflictHandler(
            () => db![DIARY_DAY_OUTBOX_COLLECTION],
          ),
        },
        [DIARY_DAY_OUTBOX_COLLECTION]: {
          schema: rxdbModels[DIARY_DAY_OUTBOX_COLLECTION].schema,
        },
        [RECIPES_COLLECTION]: {
          schema: rxdbModels[RECIPES_COLLECTION].schema,
        },
        [USER_FOODS_COLLECTION]: {
          schema: rxdbModels[USER_FOODS_COLLECTION].schema,
        },
        [GOALS_HISTORY_COLLECTION]: {
          schema: rxdbModels[GOALS_HISTORY_COLLECTION].schema,
          migrationStrategies: rxdbModels[GOALS_HISTORY_COLLECTION].migrationStrategies,
        },
        [WEIGHT_HISTORY_COLLECTION]: {
          schema: rxdbModels[WEIGHT_HISTORY_COLLECTION].schema,
          migrationStrategies: rxdbModels[WEIGHT_HISTORY_COLLECTION].migrationStrategies,
        },
        [CUSTOM_FOOD_MIGRATIONS_COLLECTION]: {
          schema: rxdbModels[CUSTOM_FOOD_MIGRATIONS_COLLECTION].schema,
        },
        [SETTINGS_COLLECTION]: {
          schema: rxdbModels[SETTINGS_COLLECTION].schema,
        },
        [MEALS_COLLECTION]: {
          schema: rxdbModels[MEALS_COLLECTION].schema,
          migrationStrategies: rxdbModels[MEALS_COLLECTION].migrationStrategies,
        },
      });

      return db;
    } catch (error) {
      await db?.close().catch(() => {
        // Avoid leaving a failed setup registered as a duplicate DB instance.
      });
      throw error;
    }
  })();

  dbCache.set(userId, promise);

  promise.catch(() => {
    dbCache.delete(userId);
  });

  return promise;
}

/**
 * Convenience helper for code that doesn't receive `userId` directly.
 * Reads the current user from authSessionStore.
 */
export async function getDbForCurrentUser(): Promise<AppDatabase> {
  // Dynamic import avoids a circular dep with the auth store module.
  const { useAuthSessionStore } = await import('@/stores/authSessionStore');
  const userId = useAuthSessionStore().user?.id;
  if (!userId) throw new Error('[rxdb] No authenticated user');
  return getDb(userId);
}

export async function closeDb(userId: string): Promise<void> {
  const cached = dbCache.get(userId);
  if (!cached) return;
  dbCache.delete(userId);
  try {
    const db = await cached;
    await db.close();
  } catch {
    // best-effort close
  }
}
